import { Lock } from "lucide-react"
import type * as React from "react"

import { Inactive } from "@/components/ui/inactive"
import { EMPTY } from "@/lib/format"
import { cn } from "@/lib/utils"

import { field, type Dict, type ProfileState } from "./card-data"

/**
 * Панель Summary: та же оболочка, что у остальных экранов консоли
 * (`rounded border border-line bg-surface shadow-panel`, шапка на линии). Своей
 * рамки для карточки не изобретаем — аналитик ходит между экранами, и вторая
 * форма панели читалась бы как другой раздел.
 */
export function Panel({
  title,
  right,
  children,
  className,
}: {
  title: string
  right?: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <section
      className={cn(
        "animate-panel-in rounded border border-line bg-surface shadow-panel",
        className,
      )}
    >
      <header className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
        <h3 className="section-title">{title}</h3>
        {right}
      </header>
      <div className="px-4 py-3">{children}</div>
    </section>
  )
}

/** Сетка «подпись/значение» — общий контейнер для полей внутри панели. */
export function Fields({
  children,
  cols = 2,
}: {
  children: React.ReactNode
  cols?: 1 | 2 | 3
}) {
  const grid = cols === 1 ? "grid-cols-1" : cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"
  return <dl className={cn("grid grid-cols-1 gap-x-6 gap-y-3", grid)}>{children}</dl>
}

/**
 * Поле, читающее значение из словаря по имени колонки. Отсутствующая или пустая
 * колонка даёт «—», а не пустое место: пустое место читается как «съехала
 * вёрстка», прочерк — как «данных нет».
 */
export function DataField({
  source,
  k,
  label,
  mono = false,
}: {
  source: Dict
  k: string
  label: string
  mono?: boolean
}) {
  const value = field(source, k)
  return (
    <div className="min-w-0">
      <dt className="stencil-label text-fg-subtle">{label}</dt>
      <dd className={cn(mono ? "font-mono text-[13px]" : "text-sm", "break-words text-fg")}>
        {value ?? EMPTY}
      </dd>
    </div>
  )
}

/** Поле с уже готовым значением (агрегаты, отформатированные числа). */
export function Field({
  label,
  value,
  mono = false,
}: {
  label: string
  value: React.ReactNode
  mono?: boolean
}) {
  return (
    <div className="min-w-0">
      <dt className="stencil-label text-fg-subtle">{label}</dt>
      <dd className={cn(mono ? "font-mono text-[13px]" : "text-sm", "break-words text-fg")}>
        {value ?? EMPTY}
      </dd>
    </div>
  )
}

/**
 * Строка-флаг: точка + подпись + состояние. Три состояния разведены цветом:
 * `true` — красный (сигнал), `false` — приглушённый «no», `null` — «—»
 * (неизвестно). Ровный `no` не подсвечиваем: спокойное состояние не должно
 * спорить за внимание с настоящим сигналом.
 */
export function Flag({ label, on }: { label: string; on: boolean | null }) {
  const dot = on === true ? "bg-decline" : on === false ? "bg-approve/60" : "bg-line-strong"
  const tone = on === true ? "text-decline" : on === false ? "text-fg-muted" : "text-fg-subtle"
  const text = on === true ? "yes" : on === false ? "no" : EMPTY
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <span className="flex items-center gap-1.5">
        <span className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full", dot)} />
        <span className="text-xs text-fg">{label}</span>
      </span>
      <span className={cn("text-xs font-semibold uppercase tracking-[0.04em]", tone)}>
        {text}
      </span>
    </div>
  )
}

/**
 * Набор зарегистрированных профилей чипами. Показываются только те, о которых
 * что-то известно: `null` (колонки нет / пусто) не рисуется вовсе — иначе
 * неизвестный профиль читался бы как «точно не зарегистрирован». Известный «да»
 * — акцентный чип, известный «нет» — приглушённый.
 */
export function ProfileChips({ items }: { items: ProfileState[] }) {
  const known = items.filter((item) => item.on !== null)
  if (known.length === 0) {
    return <span className="text-xs text-fg-subtle">{EMPTY}</span>
  }
  return (
    <div className="flex flex-wrap gap-1">
      {known.map((item) => (
        <span
          key={item.label}
          className={cn(
            "rounded-sm border px-1.5 py-px text-xs font-medium",
            item.on
              ? "border-accent/35 bg-accent-soft text-accent"
              : "border-line bg-surface-sunken text-fg-subtle line-through",
          )}
        >
          {item.label}
        </span>
      ))}
    </div>
  )
}

/**
 * Заглушка-панель: место на карточке образца занято, но данных за ним в этой
 * сборке нет. Не пустая рамка и не «скоро»: по наведению прямым текстом сказано,
 * что блок не реализован, — чтобы занятое место не читалось как «данных нет».
 */
export function MockPanel({ title, reason }: { title: string; reason: string }) {
  return (
    <Panel
      title={title}
      right={
        <Inactive reason={reason} size="sm" variant="ghost">
          <Lock className="h-3 w-3" strokeWidth={2} />
          не реализовано
        </Inactive>
      }
    >
      <p className="text-xs leading-relaxed text-fg-subtle">{reason}</p>
    </Panel>
  )
}
